import type { BalanceSheetHiddenReason } from "./budgetEditorBalanceSheetTypes";

/**
 * Account-name keyword lists for the Budget Editor balance sheet rules.
 *
 * @remarks
 * Names are the fallback evidence while the provider account columns are
 * null. Every phrase is matched on word boundaries against a normalised name
 * (lower case, apostrophes dropped, punctuation and digit runs split into
 * spaces), with an optional plural ending, so "pre-tax" never matches "tax"
 * and "Superior Equipment" never matches "super". Phrases are deliberately
 * specific: a single common word is only listed where Kate's logic doc names
 * it. Keywords are never shown to the owner.
 */

/** Lower-cases a name and splits it into space separated words. */
export const normaliseBalanceSheetName = (name: string | null | undefined) =>
  (name ?? "")
    .toLowerCase()
    .replace(/['’`]/g, "")
    .replace(/([a-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([a-z])/g, "$1 $2")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const escapePhrase = (phrase: string) =>
  phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const matcherCache = new WeakMap<readonly string[], RegExp>();

const getMatcher = (phrases: readonly string[]): RegExp => {
  const cached = matcherCache.get(phrases);
  if (cached) return cached;
  const source = phrases
    .map(
      (phrase) => `${escapePhrase(normaliseBalanceSheetName(phrase))}(?:e?s)?`,
    )
    .join("|");
  const matcher = new RegExp(`\\b(?:${source})\\b`);
  matcherCache.set(phrases, matcher);
  return matcher;
};

/** True when any phrase appears in the name as whole words. */
export const matchesBalanceSheetKeywords = (
  phrases: readonly string[],
  name: string | null | undefined,
): boolean => {
  const normalised = normaliseBalanceSheetName(name);
  return normalised !== "" && getMatcher(phrases).test(normalised);
};

/** Taxes on the Tax checklist that no structured provider field identifies. */
export const TAX_KEYWORDS = [
  "provisional tax",
  "terminal tax",
  "preliminary tax",
  "income tax",
  "company tax",
  "corporation tax",
  "corporate tax",
  "franchise tax",
  "sales tax",
  "fbt",
  "fringe benefit tax",
  "fringe benefits tax",
  "payg instalment",
  "payg installment",
  "acc levy",
  "acc levies",
  "tax reserve",
  "output tax",
  "input tax",
  "gst",
  "vat",
  "hst",
  "qst",
  "pst",
] as const;

/** Only a tax in Australia, where it is paid to the state (elsewhere it is payroll). */
export const AU_ONLY_TAX_KEYWORDS = ["payroll tax"] as const;

export const OWNER_CONTRIBUTION_KEYWORDS = [
  "funds introduced",
  "capital introduced",
  "contribution",
  "share capital",
  "paid in capital",
  "shares issued",
  "owner investment",
  "owners investment",
  "capital",
  "common stock",
  "preferred stock",
] as const;

export const OWNER_DRAWINGS_KEYWORDS = [
  "drawing",
  "draw",
  "distribution",
  "estimated tax",
  "owner withdrawal",
] as const;

/**
 * Narrower than the equity list: on a liability, "draw" and "distribution" more
 * often mean a loan drawdown or a supplier, so only these names count.
 */
export const LIABILITY_DRAWINGS_KEYWORDS = [
  "drawings",
  "owner drawing",
  "owner withdrawal",
] as const;

export const LIABILITY_DRAWINGS_EXCLUSION_KEYWORDS = [
  "loan",
  "mortgage",
] as const;

export const DIVIDEND_KEYWORDS = ["dividend"] as const;

/** Director, shareholder, partner or beneficiary current accounts. */
export const CURRENT_ACCOUNT_KEYWORDS = [
  "current account",
  "shareholder",
  "stockholder",
  "director",
  "beneficiary",
] as const;

/** Plain "advance"; see the exclusions applied in the placement rules. */
export const OWNER_ADVANCE_KEYWORDS = ["advance"] as const;

/** A bare "reserve" is a non-cash account only on equity. */
export const EQUITY_RESERVE_KEYWORDS = ["reserve"] as const;

/** A name with one of these is a fee or a staff item, not a current account. */
export const CURRENT_ACCOUNT_EXCLUSION_KEYWORDS = [
  "fee",
  "remuneration",
  "salary",
  "bonus",
  "staff",
  "employee",
  "customer",
  "client",
  "supplier",
  "wages",
] as const;

/** Payment processors; only assets with these names are clearing accounts. */
export const PAYMENT_PROCESSOR_KEYWORDS = [
  "stripe",
  "paypal",
  "square",
  "shopify",
  "eftpos",
  "undeposited",
] as const;

/** Clearing in any form, assets and liabilities. */
export const CLEARING_KEYWORDS = ["clearing"] as const;

export const CREDIT_CARD_KEYWORDS = [
  "credit card",
  "visa",
  "mastercard",
  "amex",
  "american express",
] as const;

export const PAYROLL_KEYWORDS = [
  "paye",
  "kiwisaver",
  "esct",
  "student loan",
  "withholding",
  "paygw",
  "super",
  "superannuation",
  "nic",
  "pension",
  "prsi",
  "usc",
  "source deductions",
  "cpp",
  "ei",
  "futa",
  "suta",
  "wages",
  "payroll",
  "holiday pay",
  "annual leave",
  "long service leave",
  "cis",
  "rct",
] as const;

/** Other accounts whose money is already in Revenue or Expenses, or never moves. */
export const HIDDEN_ACCOUNT_KEYWORDS: Readonly<
  Partial<Record<BalanceSheetHiddenReason, readonly string[]>>
> = {
  "petty-cash": ["petty cash", "cash on hand", "float"],
  "inventory-or-prepayment": [
    "prepaid",
    "prepayment",
    "inventory",
    "stock on hand",
  ],
  "accrual-or-deferral": [
    "accrued income",
    "accrued revenue",
    "accrued liabilities",
    "accrued expenses",
    "accrued costs",
    "unbilled",
    "work in progress",
    "income in advance",
    "revenue in advance",
    "unearned revenue",
    "deferred revenue",
    "customer deposits",
    "gift card",
    "gift voucher",
  ],
  "non-cash-account": [
    "accumulated depreciation",
    "accumulated amortisation",
    "accumulated amortization",
    "right of use",
    "doubtful debts",
    "bad debt allowance",
    "deferred tax",
    "deferred income tax",
    "revaluation reserve",
    "retained earnings",
    "current year earnings",
    "opening balance equity",
    "unexpired interest",
    "rwt",
    "imputation",
    "rounding",
    "historical adjustment",
    "tracking transfers",
  ],
  suspense: ["suspense"],
  "trust-money": ["held in trust"],
};

/** Assets and liabilities rows from the Shown accounts tab, by name. */
export const ASSET_KEYWORDS = {
  bondsPaid: ["bond", "security deposit", "deposits paid", "rental bond"],
  intangible: [
    "goodwill",
    "software",
    "website",
    "licence",
    "license",
    "customer list",
    "intangible",
    "trademark",
    "patent",
  ],
  investment: ["investment", "shares in", "share portfolio", "managed fund"],
  loansToOthers: [
    "loan to",
    "staff loan",
    "employee loan",
    "employee advance",
    "advance to",
    "advances to",
    "intercompany",
    "related party loan",
  ],
  retention: ["retention", "retainage"],
  termDeposit: ["term deposit", "savings"],
} as const;

export const LIABILITY_KEYWORDS = {
  governmentLoan: [
    "government loan",
    "eidl",
    "covid loan",
    "small business cashflow",
  ],
  hirePurchase: ["hire purchase", "hp", "finance lease"],
  lease: ["lease"],
  shortTerm: [
    "line of credit",
    "credit line",
    "merchant cash advance",
    "cash advance",
    "buy now pay later",
    "afterpay",
    "short term loan",
  ],
} as const;

const TAX_WORD = "(?:gst|vat|hst|qst|pst|sales tax)";
const TAX_QUALIFIERS = [
  new RegExp(
    `\\b(?:incl|inc|including|excl|exc|excluding|ex|plus|less) (?:of )?${TAX_WORD}\\b`,
    "g",
  ),
  new RegExp(
    `\\b${TAX_WORD} (?:incl|inc|including|excl|exc|excluding|inclusive|exclusive|included|excluded)\\b`,
    "g",
  ),
];

/**
 * Removes parentheticals and incl/excl qualifiers, as long as another word
 * remains, so a tax word that only qualifies the amount ("Prepaid expenses incl GST", "Gift cards (GST)") is not
 * mistaken for the account's subject.
 */
const stripTaxQualifiers = (name: string | null | undefined): string => {
  const stripped = TAX_QUALIFIERS.reduce(
    (text, pattern) => text.replace(pattern, " "),
    normaliseBalanceSheetName((name ?? "").replace(/\([^)]*\)/g, " ")),
  ).trim();
  // Nothing else left ("GST incl", "Less GST"): the tax word is the subject.
  return stripped === "" ? normaliseBalanceSheetName(name) : stripped;
};

/**
 * True when the name's subject is a tax keyword (payroll tax counts only in
 * AU). A tax word used only as a qualifier does not count.
 */
export const matchesTaxKeywords = (
  name: string | null | undefined,
  country?: string | null,
): boolean => {
  const subject = stripTaxQualifiers(name);
  return (
    matchesBalanceSheetKeywords(TAX_KEYWORDS, subject) ||
    ((country ?? "").trim().toUpperCase() === "AU" &&
      matchesBalanceSheetKeywords(AU_ONLY_TAX_KEYWORDS, subject))
  );
};
